/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Change, ChangeOptions, ObservableList, ObserveModes, toArray, hasValue } from '@typescript-kit/core';
import {
  AccountingEntityData,
  AccountingEntityModel, AccountingRecordModel,
} from '@teamworks/global';
import { MonthSelectViewData, MonthSelectViewFieldKey, MonthSelectViewModel } from '../../shared/view/month-select.view-model';

export interface AccountingCommonViewData extends MonthSelectViewData {
  accountingEntityList?: Iterable<AccountingEntityData>;
  accountingEntityId?: number;
  accountingEntity?: AccountingEntityData;
  totalAllocatedCosts?: number;
  salesTax?: number;
  invoiceAmount?: number;
  totalOperatingCosts?: number;
  inverseOperatingCosts?: number;
  contributionMargin?: number;
  contributionMarginRate?: number;
}

export class AccountingCommonViewFieldKey extends MonthSelectViewFieldKey {
  static readonly ACCOUNTING_ENTITY_LIST = 'accountingEntityList';
  static readonly ACCOUNTING_ENTITY_ID = 'accountingEntityId';
  static readonly ACCOUNTING_ENTITY = 'accountingEntity';
  static readonly TOTAL_ALLOCATED_COSTS = 'totalAllocatedCosts';
  static readonly SALES_TAX = 'salesTax';
  static readonly INVOICE_AMOUNT = 'invoiceAmount';
  static readonly TOTAL_OPERATING_COSTS = 'totalOperatingCosts';
  static readonly INVERSE_OPERATING_COSTS = 'inverseOperatingCosts';
  static readonly CONTRIBUTION_MARGIN = 'contributionMargin';
  static readonly CONTRIBUTION_MARGIN_RATE = 'contributionMarginRate';
}

export class AccountingCommonViewModel extends MonthSelectViewModel implements AccountingCommonViewData {
  static readonly KEYS = MonthSelectViewModel.KEYS.concat(
    Object.keys(AccountingCommonViewFieldKey).map((key) => AccountingCommonViewFieldKey[key])
  );

  constructor(values?: AccountingCommonViewData) {
    super(values, AccountingCommonViewModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get accountingEntityList(): ObservableList<AccountingEntityModel> {
    return this.get(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST);
  }

  set accountingEntityList(value: ObservableList<AccountingEntityModel>) {
    this.set(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST, value);
  }

  get accountingEntityId(): number {
    return this.get(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_ID);
  }

  set accountingEntityId(value: number) {
    this.set(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_ID, value);
  }

  get accountingEntity(): AccountingEntityModel {
    return this.get(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY);
  }

  set accountingEntity(value: AccountingEntityModel) {
    this.set(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY, value);
  }

  get accountingRecord(): AccountingRecordModel {
    return this.accountingEntity && this.accountingEntity.record;
  }

  get totalAllocatedCosts(): number {
    return this.get(AccountingCommonViewFieldKey.TOTAL_ALLOCATED_COSTS);
  }

  set totalAllocatedCosts(value: number) {
    this.set(AccountingCommonViewFieldKey.TOTAL_ALLOCATED_COSTS, value);
  }

  get salesTax(): number {
    return this.get(AccountingCommonViewFieldKey.SALES_TAX);
  }

  set salesTax(value: number) {
    this.set(AccountingCommonViewFieldKey.SALES_TAX, value);
  }

  get invoiceAmount(): number {
    return this.get(AccountingCommonViewFieldKey.INVOICE_AMOUNT);
  }

  set invoiceAmount(value: number) {
    this.set(AccountingCommonViewFieldKey.INVOICE_AMOUNT, value);
  }

  get inverseOperatingCosts(): number {
    return this.get(AccountingCommonViewFieldKey.INVERSE_OPERATING_COSTS);
  }

  set inverseOperatingCosts(value: number) {
    this.set(AccountingCommonViewFieldKey.INVERSE_OPERATING_COSTS, value);
  }

  get totalOperatingCosts(): number {
    return this.get(AccountingCommonViewFieldKey.TOTAL_OPERATING_COSTS);
  }

  set totalOperatingCosts(value: number) {
    this.set(AccountingCommonViewFieldKey.TOTAL_OPERATING_COSTS, value);
  }

  get contributionMargin(): number {
    return this.get(AccountingCommonViewFieldKey.CONTRIBUTION_MARGIN);
  }

  set contributionMargin(value: number) {
    this.set(AccountingCommonViewFieldKey.CONTRIBUTION_MARGIN, value);
  }

  get contributionMarginRate(): number {
    return this.get(AccountingCommonViewFieldKey.CONTRIBUTION_MARGIN_RATE);
  }

  set contributionMarginRate(value: number) {
    this.set(AccountingCommonViewFieldKey.CONTRIBUTION_MARGIN_RATE, value);
  }

  setValues(values: AccountingCommonViewData, options?: ChangeOptions): { [p: string]: Change<any> } {
    return super.setValues(values, options);
  }

  protected setupValues(values: AccountingCommonViewData, exclusive: boolean): AccountingCommonViewData {
    this.setupAccountingEntityList(values, exclusive);
    if (AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_ID in values || AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST in values) {
      const accountingEntityList: ObservableList<AccountingEntityModel> = this.getSetupValue(
        values, AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST, exclusive
      );
      const accountingEntityId = this.getSetupValue(
        values, AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_ID, exclusive
      );
      values[AccountingCommonViewFieldKey.ACCOUNTING_ENTITY] = hasValue(accountingEntityId)
        && accountingEntityList.firstValue((item) => item.index === accountingEntityId) || null;
    }
    return super.setupValues(values, exclusive) as AccountingCommonViewData;
  }

  private setupAccountingEntityList(values: AccountingCommonViewData, exclusive: boolean) {
    if (!exclusive && !(AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST in values)
      || values[AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST] instanceof ObservableList) {
      return;
    }
    const modelList = toArray(values[AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST])
      .map((data) => data instanceof AccountingEntityModel ? data : new AccountingEntityModel(data));
    let currentValue: ObservableList<AccountingEntityModel>;
    const originalValue = this.accountingEntityList;
    if (originalValue) {
      originalValue.setValues(modelList, { exclusive: true });
      currentValue = originalValue;
    } else {
      currentValue = new ObservableList(modelList, { observeMode: ObserveModes.FLAT });
    }
    values[AccountingCommonViewFieldKey.ACCOUNTING_ENTITY_LIST] = currentValue;
  }

}
